import { config } from 'lib-shared-backend-config';
import {
  MessageAttachedFile,
  MessageInlineImage,
} from 'lib-shared-legacy-types';
import { isUUID } from 'validator';

const UNITS = ['B', 'KB', 'MB'];

const imageExtensions: string[] = [
  'jpg',
  'jpeg',
  'bmp',
  'gif',
  'tiff',
  'png',
  'webp',
];
const pdfExtensions: string[] = ['pdf'];
const xlsxExtensions: string[] = ['xlsx'];
const videoExtensions: string[] = ['mp4', 'ogg', 'webm'];
const previewableExtensions: string[] = pdfExtensions.concat(
  imageExtensions,
  xlsxExtensions,
  videoExtensions,
);

export function getFileSize(bytes: number): string {
  for (let i = UNITS.length - 1; i >= 0; i -= 1) {
    const divider = 1024 ** i;
    const decimal = bytes / divider;
    if (i === 0 || decimal > 1) {
      return `${Math.round(decimal * 10) / 10} ${UNITS[i]}`;
    }
  }
  return '';
}

function hasExtensionIn(fileName: string, extensions: string[]): boolean {
  if (!fileName) return false;

  const extension = (fileName.split('.').pop() as string).toLowerCase();
  return extensions.includes(extension);
}

export function isImageAttachment(fileName: string): boolean {
  return hasExtensionIn(fileName, imageExtensions);
}

export function isPdfAttachment(fileName: string): boolean {
  return hasExtensionIn(fileName, pdfExtensions);
}

export function isExcelAttachment(fileName: string): boolean {
  return hasExtensionIn(fileName, xlsxExtensions);
}

export function isVideoAttachment(fileName: string): boolean {
  return hasExtensionIn(fileName, videoExtensions);
}

export function isPreviewableAttachment(fileName: string): boolean {
  return hasExtensionIn(fileName, previewableExtensions);
}

export type AttachmentFile = {
  name: string;
  absolute_uri: string;
  relative_uri: string;
};

const prepareAttachmentArray = (
  type: string,
  attachmentArray: MessageAttachedFile[] | MessageInlineImage[] | string[],
  prefix: string,
): AttachmentFile[] => {
  const reg = /^https?:\/\/.*?\//i;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return attachmentArray.reduce(
    (
      acc: AttachmentFile[],
      file: MessageAttachedFile | MessageInlineImage | string,
      index,
    ) => {
      let url: string;
      let name: string;

      if (typeof file === 'string') {
        url = file;
        const filename = file.split('/').pop() as string;
        name = isUUID(filename) ? `${type}_${index}` : filename;
      } else if (typeof file === 'object') {
        url = file.url;
        const prettyName = file.pretty_name;
        if (prettyName) {
          name = isUUID(prettyName) ? `${type}_${index}` : prettyName;
        } else {
          name = `${type}_${index}`;
        }
      } else {
        return acc;
      }

      let absolute_uri: string;
      let relative_uri: string;

      if (url && url.match(reg)) {
        absolute_uri = url;
        relative_uri = url.replace(reg, '/');
      } else if (url) {
        absolute_uri = `${prefix}${url}`;
        relative_uri = url;
      }

      return [
        ...acc,
        {
          name,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          absolute_uri,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          relative_uri,
        },
      ];
    },
    [],
  );
};

export function prepareAttachments(
  attached_files:
    | MessageAttachedFile
    | MessageAttachedFile[]
    | null
    | undefined,
): AttachmentFile[] {
  if (!attached_files) {
    return [];
  }

  const attachments: MessageAttachedFile[] = Array.isArray(attached_files)
    ? attached_files
    : [attached_files];

  return prepareAttachmentArray('attachment', attachments, config.url.files);
}

export function prepareInlineImages(
  inline_images?: MessageInlineImage[] | null,
): AttachmentFile[] {
  if (inline_images && Array.isArray(inline_images)) {
    return prepareAttachmentArray(
      'image',
      inline_images,
      config.url.userContent,
    );
  }
  return [];
}
